import styled, { CSSObject } from '@emotion/styled'
import { colors, spacing, font, radius, shadow } from '@mobi/component-library/Foundations'

type Props = {
  invalid?: boolean
  disabled?: boolean
  isPlaceholderSelected?: boolean
  isOpen?: boolean
  isLegacyDropdown?: boolean
}

function determineColors(isDisabled: boolean, isInvalid: boolean, isOpen?: boolean) {
  if (isDisabled) {
    return {
      background: colors.surface[100],
      border: colors.neutral[400],
      helpText: colors.neutral[400],
    }
  }

  if (isInvalid) {
    return {
      background: colors.error[50],
      border: colors.error[600],
      helpText: colors.error[600],
    }
  }

  return {
    background: colors.white,
    border: isOpen ? colors.lavender[300] : colors.neutral[300],
    helpText: colors.neutral[600],
  }
}

function getSelectColor(disabled: boolean, isPlaceholderSelected: boolean) {
  if (disabled) {
    return colors.neutral[400]
  }
  if (isPlaceholderSelected) {
    return colors.neutral[500]
  }
  return colors.black
}

export const DropdownInputStyled = styled.div<Props>(
  ({
    invalid = false,
    disabled = false,
    isPlaceholderSelected = false,
    isOpen = false,
    isLegacyDropdown = false,
  }) => {
    const effectiveColors = determineColors(disabled, invalid, isOpen)
    const baseStyles: CSSObject = {
      position: 'relative',
      color: disabled ? colors.neutral[600] : colors.black,
      fontFamily: font.family.primary,
      ...font.size.lg,

      '& > label': {
        display: 'block',
        fontWeight: font.weight.medium,
        ...font.size.lg,
        marginBottom: spacing.smx2,
      },

      '& > span:last-child': {
        color: effectiveColors.helpText,
        display: 'block',
        marginTop: spacing.smx1,
        ...font.size.md,

        '&:empty': { display: 'none' },
      },
    }

    if (!isLegacyDropdown) return baseStyles

    return {
      ...baseStyles,

      '& > div': {
        alignItems: 'center',
        backgroundColor: effectiveColors.background,
        border: `1px solid ${isOpen ? colors.lavender[300] : effectiveColors.border}`,
        borderRadius: radius.lgx1,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: shadow.xs,
        height: '4.4rem',

        '&:focus-within': {
          borderColor: colors.lavender[300],
          outlineColor: colors.lavender[50],
        },

        select: {
          appearance: 'none',
          '-webkit-appearance': 'none',
          '-moz-appearance': 'none',
          width: '100%',
          background: 'none',
          border: 'none',
          caretColor: colors.studio[500],
          color: getSelectColor(disabled, isPlaceholderSelected),
          flex: '1 1 auto',
          font: 'inherit',
          fontSize: 'inherit',
          fontWeight: font.weight.regular,
          height: '100%',
          letterSpacing: 'inherit',
          lineHeight: 'inherit',

          '&:not(:first-child)': {
            marginInlineStart: spacing.sm,

            '@supports not (margin-inline-start: 0)': { marginLeft: spacing.md },
          },

          '&:focus': { outline: 'none' },

          option: {
            color: colors.black,
          },
        },
      },

      '& > div > :first-child': {
        paddingLeft: spacing.md,
      },
    }
  }
)

export const DropdownChevron = styled.div({
  span: {
    position: 'absolute',
    pointerEvents: 'none',
    right: spacing.md,
    top: '50%',
    transform: 'translateY(-50%)',
  },
})

export const DropdownTrigger = styled.button<Props>(
  ({ invalid = false, disabled = false, isPlaceholderSelected = false }) => {
    const effectiveColors = determineColors(disabled, invalid)

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      backgroundColor: effectiveColors.background,
      border: `1px solid ${effectiveColors.border}`,
      borderRadius: radius.lgx1,
      boxShadow: shadow.xs,
      height: '4.4rem',
      padding: `0 ${spacing.md}`,
      cursor: disabled ? 'default' : 'pointer',
      color: getSelectColor(disabled, isPlaceholderSelected),
      fontFamily: font.family.primary,
      ...font.size.lg,
      fontWeight: font.weight.regular,
      textAlign: 'left',
      WebkitTapHighlightColor: 'transparent',

      '&:focus': {
        borderColor: invalid ? effectiveColors.border : colors.lavender[300],
        boxShadow: `0 0 0 4px ${invalid ? colors.error[50] : colors.lavender[50]}, ${shadow.xs}`,
      },

      '& > span:first-of-type': {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    }
  }
)

export const IconWrapper = styled.span<{ $rotate?: boolean }>(({ $rotate }) => ({
  display: 'flex',
  color: colors.black,
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 150ms ease',
  transform: $rotate ? 'rotate(180deg)' : 'rotate(0deg)',
  marginLeft: spacing.sm,
  flexShrink: 0,
}))

export const Divider = styled.hr({
  border: 'none',
  borderTop: `1px solid ${colors.neutral[200]}`,
  margin: `${spacing.smx2} ${spacing.none}`,
})

export const OptionContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx2,
  alignItems: 'flex-start',
  flex: 1,
  minWidth: 0,
})

export const OptionLabel = styled.span({
  ...font.size.lg,
  fontWeight: font.weight.regular,
  color: colors.black,
})

export const OptionSubText = styled.span({
  ...font.size.md,
  fontWeight: font.weight.regular,
  color: colors.neutral[900],
})

// ======================
// Searchable Dropdown Styles
// ======================

type SearchInputWrapperProps = {
  isOpen?: boolean
  invalid?: boolean
  disabled?: boolean
}

export const SearchInputWrapper = styled.div<SearchInputWrapperProps>(
  ({ isOpen = false, invalid = false, disabled = false }) => {
    const effectiveColors = determineColors(disabled, invalid, isOpen)

    return {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: effectiveColors.background,
      border: `1px solid ${effectiveColors.border}`,
      borderRadius: radius.lgx1,
      boxShadow: shadow.xs,
      height: '4.4rem',
      padding: `0 ${spacing.md}`,
      gap: spacing.sm,
      cursor: disabled ? 'default' : 'text',

      '&:focus-within': {
        borderColor: invalid ? effectiveColors.border : colors.lavender[300],
        boxShadow: `0 0 0 4px ${invalid ? colors.error[50] : colors.lavender[50]}, ${shadow.xs}`,
      },
    }
  }
)

export const SearchInput = styled.input(() => ({
  flex: 1,
  border: 'none',
  background: 'none',
  outline: 'none',
  fontFamily: font.family.primary,
  ...font.size.lg,
  fontWeight: font.weight.regular,
  color: colors.black,
  caretColor: colors.studio[500],
  minWidth: 0,

  '&::placeholder': {
    color: colors.neutral[500],
  },

  '&:disabled': {
    color: colors.neutral[400],
    cursor: 'default',

    '&::placeholder': {
      color: colors.neutral[400],
    },
  },
}))

export const ClearButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  padding: 0,
})
